import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../api/client.js'
import { useConversationsStore } from '../store/conversationsStore.js'
import type {
  ActivityLogEntry,
  ConversationDetailResponse,
  Message,
  MessageApproval,
  MessageAttachment,
} from '../modules/conversations/types.js'

const EMPTY_MESSAGES: Message[] = []
// Module-level stable fallback — avoids a fresh [] reference / render loop
// when the detail response omits messageApprovals.
const EMPTY_APPROVALS: MessageApproval[] = []

export interface UseConversationReturn {
  conversation: ConversationDetailResponse['conversation'] | null
  messages: Message[]
  noteAttachments: MessageAttachment[]
  conversationAttachments: MessageAttachment[]
  activityLog: ActivityLogEntry[]
  loading: boolean
  error: string | null
  appendMessage: (message: Message) => void
  appendActivity: (entry: ActivityLogEntry) => void
  replaceMessage: (oldId: string, newMessage: Message) => void
  patchMessage: (messageId: string, patch: Partial<Message>) => void
  markMessageFailed: (messageId: string, error: string) => void
  refetch: () => Promise<void>
}

export function useConversation(selectedId: string | null): UseConversationReturn {
  const [conversation, setConversation] = useState<
    ConversationDetailResponse['conversation'] | null
  >(null)
  const [activityLog, setActivityLog] = useState<ActivityLogEntry[]>([])
  const [noteAttachments, setNoteAttachments] = useState<MessageAttachment[]>([])
  const [conversationAttachments, setConversationAttachments] = useState<MessageAttachment[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const messages = useConversationsStore((s) =>
    selectedId !== null
      ? (s.messagesByConversationId[selectedId] ?? EMPTY_MESSAGES)
      : EMPTY_MESSAGES,
  )
  const setMessages = useConversationsStore((s) => s.setMessages)
  const appendStoreMessage = useConversationsStore((s) => s.appendMessage)
  const replaceStoreMessage = useConversationsStore((s) => s.replaceMessage)
  const patchStoreMessage = useConversationsStore((s) => s.patchMessage)
  const markStoreMessageFailed = useConversationsStore((s) => s.markMessageFailed)
  const setMessageApprovals = useConversationsStore((s) => s.setMessageApprovals)
  const setCoAgentIds = useConversationsStore((s) => s.setCoAgentIds)
  const setConversationDetail = useConversationsStore((s) => s.setConversationDetail)
  const setRatingScale = useConversationsStore((s) => s.setRatingScale)
  const cachedConversation = useConversationsStore((s) =>
    selectedId !== null ? (s.conversationById[selectedId] ?? null) : null,
  )

  // Ref to read cached conversation without triggering re-fetch effect
  const cachedConversationRef = useRef(cachedConversation)
  cachedConversationRef.current = cachedConversation

  const doFetch = useCallback(
    async (id: string): Promise<void> => {
      abortRef.current?.abort()
      const ctrl = new AbortController()
      abortRef.current = ctrl
      setLoading(true)
      setError(null)
      try {
        const resp = await api.get<ConversationDetailResponse>(`/conversations/${id}`)
        if (ctrl.signal.aborted) return
        setConversation(resp.conversation)
        setConversationDetail(resp.conversation)
        setRatingScale(resp.ratingScale)
        setMessages(id, resp.messages)
        setActivityLog(resp.activityLog)
        setNoteAttachments(resp.noteAttachments ?? [])
        setConversationAttachments(resp.conversationAttachments ?? [])
        setMessageApprovals(resp.messageApprovals ?? EMPTY_APPROVALS)
        setCoAgentIds(id, resp.conversation.coAgentIds)
      } catch (err) {
        if (ctrl.signal.aborted) return
        setError(`Failed to load conversation: ${(err as Error).message}`)
      } finally {
        if (!ctrl.signal.aborted) setLoading(false)
      }
    },
    [setMessages, setMessageApprovals, setCoAgentIds, setConversationDetail, setRatingScale],
  )

  useEffect(() => {
    if (selectedId === null) {
      setConversation(null)
      setActivityLog([])
      setNoteAttachments([])
      setConversationAttachments([])
      setError(null)
      return
    }
    // Instantly show cached conversation data (stale-while-revalidate) so
    // switching between conversation tabs feels instant — the fresh data
    // arrives in the background and replaces the cached version.
    const cached = cachedConversationRef.current
    if (cached) {
      setConversation(cached)
    } else {
      setConversation(null)
    }
    void doFetch(selectedId)
    return () => abortRef.current?.abort()
  }, [selectedId, doFetch])

  const appendMessage = useCallback(
    (m: Message) => {
      if (selectedId) appendStoreMessage(selectedId, m)
    },
    [selectedId, appendStoreMessage],
  )
  // The API returns the log newest-first, and readers rely on that ordering —
  // DetailStrip takes activityLog[0] as "last modified". A live entry appended
  // to the tail would be the newest event sorted as the oldest.
  const appendActivity = useCallback((entry: ActivityLogEntry) => {
    setActivityLog((prev) => [entry, ...prev])
  }, [])
  const replaceMessage = useCallback(
    (oldId: string, newM: Message) => {
      if (selectedId) replaceStoreMessage(selectedId, oldId, newM)
    },
    [selectedId, replaceStoreMessage],
  )
  const patchMessage = useCallback(
    (messageId: string, patch: Partial<Message>) => {
      if (selectedId) patchStoreMessage(selectedId, messageId, patch)
    },
    [selectedId, patchStoreMessage],
  )
  const markMessageFailed = useCallback(
    (messageId: string, err: string) => {
      if (selectedId) markStoreMessageFailed(selectedId, messageId, err)
    },
    [selectedId, markStoreMessageFailed],
  )
  const refetch = useCallback(async (): Promise<void> => {
    if (selectedId !== null) await doFetch(selectedId)
  }, [selectedId, doFetch])

  return {
    conversation,
    messages,
    noteAttachments,
    conversationAttachments,
    activityLog,
    loading,
    error,
    appendMessage,
    appendActivity,
    replaceMessage,
    patchMessage,
    markMessageFailed,
    refetch,
  }
}
